import * as React from 'react'
import { Plus, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import {
  AUTH_PERSONS,
  type AuthPerson,
} from '@/features/tools/components/authenticate-caller/authConstants'
import {
  ORG_DEFAULT_LABEL,
  RESET_TO_ORG_DEFAULT_LABEL,
  formatPersonLabel,
} from './authenticationDraft'

// ---- Sub-components ----

function PersonChip({
  label,
  active,
  onToggle,
  onRemove,
  disabled,
}: {
  label: string
  active: boolean
  onToggle: () => void
  onRemove?: () => void
  disabled?: boolean
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors',
        active ? 'bg-indigo-50 text-slate-800' : 'bg-slate-100 text-slate-400',
        disabled && 'opacity-60'
      )}>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className={cn(
          'inline-flex items-center gap-1.5',
          !disabled && 'cursor-pointer hover:opacity-80'
        )}
        aria-pressed={active}
        aria-label={`Toggle ${label}`}>
        {label}
        {active && <span className="text-xs">✓</span>}
      </button>
      {onRemove && !disabled && (
        <button
          type="button"
          onClick={onRemove}
          className="text-slate-400 hover:text-slate-600"
          aria-label={`Remove ${label}`}>
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  )
}

function AddCustomPersonInput({
  onAdd,
  disabled,
  existing,
}: {
  onAdd: (person: string) => void
  disabled?: boolean
  existing: Set<string>
}) {
  const [value, setValue] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)

  const submit = () => {
    const normalized = value.trim().toLowerCase().replace(/\s+/g, '_')
    if (!normalized) return
    if (existing.has(normalized)) {
      setError('Person already added')
      return
    }
    onAdd(normalized)
    setValue('')
    setError(null)
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value}
          disabled={disabled}
          placeholder="Add custom person (e.g. sibling)"
          onChange={(e) => {
            setValue(e.target.value)
            setError(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
          className="min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 disabled:opacity-50"
        />
        <button
          type="button"
          onClick={submit}
          disabled={disabled || !value.trim()}
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>
      {error && <span className="text-xs text-rose-600">{error}</span>}
    </div>
  )
}

// ---- Section ----

export function PersonsEditor({
  allowedPersons,
  personsOverridden,
  interactionDisabled,
  onTogglePerson,
  onResetToOrg,
  onAddCustomPerson,
  showInheritance = true,
}: {
  allowedPersons: string[]
  personsOverridden: boolean
  interactionDisabled: boolean
  onTogglePerson: (person: string) => void
  onResetToOrg: () => void
  onAddCustomPerson: (person: string) => void
  showInheritance?: boolean
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-slate-700">Allowed persons</h4>
        {showInheritance ? (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {personsOverridden ? (
              <>
                <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">
                  Overridden
                </span>
                {!interactionDisabled && (
                  <button
                    type="button"
                    onClick={onResetToOrg}
                    className="text-indigo-600 hover:text-indigo-800">
                    {RESET_TO_ORG_DEFAULT_LABEL}
                  </button>
                )}
              </>
            ) : (
              <span className="text-slate-400">{ORG_DEFAULT_LABEL}</span>
            )}
          </div>
        ) : null}
      </div>
      <p className="text-xs text-slate-500">
        Who can authenticate on behalf of the person on the phone.
      </p>
      <div className="flex flex-wrap gap-2">
        {/* Suggested persons */}
        {AUTH_PERSONS.map((person) => (
          <PersonChip
            key={person}
            label={formatPersonLabel(person)}
            active={allowedPersons.includes(person)}
            onToggle={() => onTogglePerson(person)}
            disabled={interactionDisabled}
          />
        ))}
        {/* Ad-hoc persons (active but not in suggested list) */}
        {allowedPersons
          .filter((p) => !AUTH_PERSONS.includes(p as AuthPerson))
          .map((person) => (
            <PersonChip
              key={person}
              label={formatPersonLabel(person)}
              active
              onToggle={() => onTogglePerson(person)}
              onRemove={() => onTogglePerson(person)}
              disabled={interactionDisabled}
            />
          ))}
      </div>
      {!interactionDisabled && (
        <AddCustomPersonInput
          disabled={interactionDisabled}
          existing={new Set([...AUTH_PERSONS, ...allowedPersons])}
          onAdd={onAddCustomPerson}
        />
      )}
    </div>
  )
}
